// The solver, thinking away from the page in a worker so the table never
// stutters. If the browser will not start a worker, it thinks on the page,
// briefly. Tests hand in the in-page thinker.

import { RULES } from "../config/rules.ts"
import { parseAnswer, type Answer, type PathVerdict, type Question } from "../boundary/messages.ts"
import type { TableState } from "../domain/engine.ts"
import { solve } from "../domain/solver.ts"
import { firstWay, type Way } from "../domain/way.ts"

export type Thinker = {
  // true: a path to the Monad exists; false: proven none; unknown: gave up
  readonly pathExists: (state: TableState) => Promise<PathVerdict>
  // the first of these tables with a way to the Monad, and that way
  readonly findWay: (states: readonly TableState[], maxNodes: number) => Promise<Way>
}

const verdictOf = (state: TableState, maxNodes: number): PathVerdict => {
  const r = solve(state, { maxNodes })
  return r.tag === "solved" ? "path" : r.tag === "dead" ? "none" : "unknown"
}

export const inlineThinker = (): Thinker => ({
  pathExists: (state) => Promise.resolve(verdictOf(state, Math.min(RULES.solver.deadEndNodes, 20_000))),
  findWay: (states, maxNodes) => Promise.resolve(firstWay(states, Math.min(maxNodes, 20_000))),
})

type Pending = { readonly settle: (a: Answer) => void; readonly giveUp: Answer }
type PathAsk = { readonly state: TableState; readonly resolve: (v: PathVerdict) => void }

// Only the newest table's path matters, and one check can take seconds. So
// one path question is with the worker at a time; a newer one replaces any
// still waiting (the old asker hears "unknown"). A way asked for never
// queues behind a pile of stale checks.
export const workerThinker = (url: URL): Thinker => {
  const fallback = inlineThinker()
  const waiting = new Map<number, Pending>()
  let seq = 0
  let worker: Worker | "none" = "none"
  let checking = false
  let nextPath: PathAsk | undefined

  const fail = (): void => {
    worker = "none"
    for (const p of waiting.values()) p.settle(p.giveUp)
    waiting.clear()
    nextPath?.resolve("unknown")
    nextPath = undefined
  }
  try {
    worker = new Worker(url, { type: "module" })
    worker.addEventListener("message", (e: MessageEvent<unknown>) => {
      const a = parseAnswer(e.data)
      if (a === undefined) return
      waiting.get(a.id)?.settle(a)
      waiting.delete(a.id)
    })
    worker.addEventListener("error", fail)
  } catch {
    worker = "none"
  }
  const ask = (q: Question, giveUp: Answer, w: Worker): Promise<Answer> =>
    new Promise((resolve) => {
      waiting.set(q.id, { settle: resolve, giveUp })
      w.postMessage(q)
    })

  const checkNext = (): void => {
    const next = nextPath
    if (checking || next === undefined || worker === "none") return
    nextPath = undefined
    checking = true
    const id = ++seq
    void ask({ id, ask: "path", state: next.state, maxNodes: RULES.solver.deadEndNodes }, { id, verdict: "unknown" }, worker).then((a) => {
      checking = false
      next.resolve("verdict" in a ? a.verdict : "unknown")
      checkNext()
    })
  }

  return {
    pathExists: (state) => {
      if (worker === "none") return fallback.pathExists(state)
      return new Promise((resolve) => {
        nextPath?.resolve("unknown")
        nextPath = { state, resolve }
        checkNext()
      })
    },
    findWay: async (states, maxNodes) => {
      if (worker === "none") return fallback.findWay(states, maxNodes)
      const id = ++seq
      const a = await ask({ id, ask: "way", states, maxNodes }, { id, way: { tag: "unknown" } }, worker)
      return "way" in a ? a.way : { tag: "unknown" }
    },
  }
}
